<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const sectionRef = ref<HTMLElement | null>(null);
const textContentRef = ref<HTMLElement | null>(null);
const imageContentRef = ref<HTMLElement | null>(null);
const btnRef = ref<HTMLElement | null>(null);

onMounted(() => {
  const mm = gsap.matchMedia();

  mm.add("(min-width: 1024px)", () => {
    // Animación de entrada para el texto (de izquierda a derecha)
    gsap.fromTo(textContentRef.value, 
      { opacity: 0, x: -60 },
      { 
        opacity: 1, 
        x: 0, 
        duration: 1.2, 
        ease: 'power3.out', 
        scrollTrigger: {
          trigger: sectionRef.value,
          start: 'top 75%',
        }
      }
    );

    // Animación de entrada para la imagen (de derecha a izquierda)
    gsap.fromTo(imageContentRef.value, 
      { opacity: 0, x: 80, scale: 0.95 },
      { 
        opacity: 1, 
        x: 0, 
        scale: 1,
        duration: 1.4, 
        ease: 'power3.out', 
        scrollTrigger: {
          trigger: sectionRef.value,
          start: 'top 75%',
        }
      }
    );
  });

  // Animación móvil (fade in simple hacia arriba)
  mm.add("(max-width: 1023px)", () => {
    gsap.fromTo([textContentRef.value, imageContentRef.value], 
      { opacity: 0, y: 40 },
      { 
        opacity: 1, 
        y: 0, 
        duration: 1, 
        stagger: 0.2,
        ease: 'power3.out', 
        scrollTrigger: {
          trigger: sectionRef.value,
          start: 'top 85%',
        }
      }
    );
  });

  onUnmounted(() => {
    mm.revert();
  });
});
</script>

<template>
  <section class="access-section" ref="sectionRef">
    <div class="access-container">
      <!-- Left Content -->
      <div class="access-text" ref="textContentRef">
        <h2>
          Disponible desde<br/>
          cualquier <span class="highlight">lugar</span>
        </h2>
        <p>
          Accede a toda la información de tus activos en tiempo real, desde cualquier dispositivo y en cualquier momento. 
        </p>
        <button class="access-btn group" ref="btnRef">
          Acceder
          <svg class="btn-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
          </svg>
        </button>
      </div>

      <!-- Right Content -->
      <div class="access-image" ref="imageContentRef">
        <img src="../assets/mockups/mockup5.png" alt="Dashboard Platform en Laptop" />
      </div>
    </div>
  </section>
</template>

<style scoped>
.access-section {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 1rem;
}

@media (min-width: 768px) {
  .access-section {
    padding: 6rem 2rem;
  }
}

.access-container {
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border-radius: 32px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

@media (min-width: 1024px) {
  .access-container {
    flex-direction: row;
    align-items: center;
  }
}

.access-text {
  flex: 1;
  padding: 3rem 2rem 2rem 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

@media (min-width: 1024px) {
  .access-text {
    padding: 6rem 1rem 6rem 5rem;
  }
}

@media (min-width: 1280px) {
  .access-text {
    padding: 6rem 2rem 6rem 6rem;
  }
}

.access-text h2 {
  font-size: 40px;
  font-weight: 800;
  color: #111827;
  line-height: 1.1;
  margin-bottom: 24px;
  letter-spacing: -0.03em;
}

@media (min-width: 1024px) {
  .access-text h2 {
    font-size: 64px;
  }
}

.highlight {
  color: #129BEF;
  font-weight: 800; /* Asegurar que no pierda el grosor */
}

.access-text p {
  font-size: 18px;
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: 40px;
  max-width: 500px;
}

@media (min-width: 1024px) {
  .access-text p {
    font-size: 20px;
    max-width: 520px;
  }
}

.access-btn {
  align-self: flex-start;
  background-color: #129BEF;
  color: #ffffff;
  font-weight: 600;
  font-size: 18px;
  padding: 16px 40px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.access-btn:hover {
  background-color: #0c82ce;
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(18, 155, 239, 0.3);
}

.btn-arrow {
  width: 20px;
  height: 20px;
  transition: transform 0.3s ease;
}

.access-btn:hover .btn-arrow {
  transform: translateX(6px);
}

.access-image {
  flex: 1.2;
  position: relative;
  min-height: 300px; /* Da un alto base en móviles para que la imagen absoluta se vea */
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  padding-top: 1rem;
}

@media (min-width: 1024px) {
  .access-image {
    min-height: auto; /* En desktop la altura la da el texto adjunto */
    padding-top: 0;
    padding-left: 0;
  }
}

.access-image img {
    position: absolute;
    right: -20px;
    top: -155px;
    width: 100%;
    max-width: 800px;
    height: 480px;
    object-fit: contain;
    object-position: bottom right;
    transform: scale(1.15);
    transform-origin: bottom right;
    border-bottom-right-radius: 32px;
}

/* Mobile / tablet: imagen en flujo normal, sin posicionamiento absoluto */
@media (max-width: 1023px) {
  .access-text {
    padding: 2.5rem 1.5rem 1.5rem;
  }

  .access-image {
    min-height: auto;
    padding: 0 1.5rem 2rem;
    justify-content: center;
    align-items: center;
  }

  .access-image img {
    position: static;
    right: auto;
    top: auto;
    width: 100%;
    max-width: 460px;
    height: auto;
    transform: none;
    object-position: center;
    border-radius: 16px;
  }
}

@media (max-width: 480px) {
  .access-text h2 {
    font-size: 32px;
  }

  .access-text p {
    font-size: 16px;
  }

  .access-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
